import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, formatApiErrorDetail } from "./api";

export default function Users() {
  const [rows, setRows] = useState([]);
  const [inv, setInv] = useState({ email: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null);
  const [err, setErr] = useState("");

  const load = () => adminApi.listUsers().then((r) => setRows(r.data?.users || []));
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault(); setBusy(true); setErr(""); setIssued(null);
    try {
      const { data } = await adminApi.inviteUser({ email: inv.email.trim().toLowerCase(), name: inv.name.trim() });
      setIssued(data);
      setInv({ email: "", name: "" });
      load();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Could not invite.");
    } finally { setBusy(false); }
  };

  const revoke = async (email) => {
    if (!confirm(`Revoke access for ${email}?`)) return;
    try { await adminApi.revokeUser(email); load(); }
    catch (e2) { alert(formatApiErrorDetail(e2.response?.data?.detail) || "Revoke failed."); }
  };

  const regen = async (email) => {
    if (!confirm(`Generate a fresh setup link for ${email}? Any previous link becomes unusable once the new one is consumed.`)) return;
    try { const { data } = await adminApi.regenerateSetupLink(email); setIssued(data); }
    catch (e2) { alert(formatApiErrorDetail(e2.response?.data?.detail) || "Could not generate link."); }
  };

  return (
    <div className="ga-page" data-testid="users-page">
      <div className="ga-eyebrow">Settings · Admins</div>
      <h1 className="ga-title">Administrators</h1>
      <p className="ga-mute" style={{ maxWidth: "60ch" }}>
        Each administrator has full access to content, enquiries and integrations. Invite additional admins by sending them a one-time setup link — never share passwords directly.
      </p>

      <div className="ga-info-card" style={{ marginTop: 24 }} data-testid="users-list">
        {rows.length === 0 && <p className="ga-mute">Loading…</p>}
        {rows.map((u) => (
          <div key={u.email} className="ga-info-card__row" data-testid={`user-row-${u.email}`}>
            <div>
              <div style={{ fontFamily: "var(--gp-font-display)", fontWeight: 600 }}>{u.name || u.email}</div>
              <div className="ga-mute" style={{ fontSize: 12 }}>{u.email} · last login {u.last_login_at || "—"} · status {u.disabled ? "Revoked" : (u.must_change_password ? "Awaiting setup" : "Active")}</div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="ga-btn ga-btn--ghost" onClick={() => regen(u.email)} data-testid={`user-regen-${u.email}`}>Setup link</button>
              <button className="ga-btn ga-btn--ghost" onClick={() => revoke(u.email)} data-testid={`user-revoke-${u.email}`}>Revoke</button>
            </div>
          </div>
        ))}
      </div>

      {issued && (
        <div className="ga-info-card" style={{ marginTop: 16, borderColor: "#D5B77A" }} data-testid="users-issued">
          <div style={{ fontFamily: "var(--gp-font-display)", fontWeight: 600, marginBottom: 8 }}>One-time setup link — copy now</div>
          <code style={{ display: "block", padding: 12, background: "rgba(213,183,122,0.08)", color: "#D5B77A", wordBreak: "break-all", fontSize: 13 }} data-testid="users-issued-url">
            {issued.login_url}
          </code>
          <p className="ga-mute" style={{ marginTop: 8, fontSize: 12 }}>
            Expires {issued.expires_at}. Send it privately (encrypted chat) — this is the only time it appears. Anyone with the link can set the password.
          </p>
        </div>
      )}

      <h2 className="ga-group__title" style={{ marginTop: 40 }}>Invite another admin</h2>
      <form onSubmit={invite} className="ga-form" style={{ maxWidth: 480 }}>
        <label className="ga-field ga-field--wide">
          <span>Email</span>
          <input type="email" required value={inv.email} onChange={(e) => setInv((s) => ({ ...s, email: e.target.value }))} data-testid="users-invite-email" />
        </label>
        <label className="ga-field ga-field--wide">
          <span>Display name (optional)</span>
          <input type="text" value={inv.name} onChange={(e) => setInv((s) => ({ ...s, name: e.target.value }))} data-testid="users-invite-name" />
        </label>
        <div className="ga-savebar">
          <div>{err && <span className="ga-error" data-testid="users-err">{err}</span>}</div>
          <button type="submit" className="ga-btn" disabled={busy || !inv.email} data-testid="users-invite-submit">
            {busy ? "Inviting…" : "Generate setup link"}
          </button>
        </div>
      </form>

      <p style={{ marginTop: 40 }}>
        <Link to="/admin/settings" className="ga-link" data-testid="users-back">← Back to Settings</Link>
      </p>
    </div>
  );
}
